import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, CheckCircle2, Lock, ClipboardList, Info, BookOpen, Table2, SlidersHorizontal, Pencil, Baby } from "lucide-react";
import { toast } from "sonner";
import GradingConfigDialog from "@/components/GradingConfigDialog";
import Assessments from "@/pages/Assessments";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const classLabel = (c) => (["LKG", "UKG", "Play Group"].includes(c) ? c : `Class ${c}`);
const defaultMax = (key) => (String(key).startsWith("MT") ? 40 : 80);

// Admin control: choose which single test is currently open for teachers to
// enter marks. Overrides the automatic month-based window.
export default function AdminAssessmentSettings() {
  const [tab, setTab] = useState("active");
  return (
    <div data-testid="admin-assessment-settings">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="assessment-subtabs">
          <TabsTrigger value="active" data-testid="assessment-subtab-active" className="gap-1.5">
            <ClipboardList className="h-4 w-4" /> Active test
          </TabsTrigger>
          <TabsTrigger value="maxmarks" data-testid="assessment-subtab-maxmarks" className="gap-1.5">
            <Table2 className="h-4 w-4" /> Max marks
          </TabsTrigger>
          <TabsTrigger value="grading" data-testid="assessment-subtab-grading" className="gap-1.5">
            <SlidersHorizontal className="h-4 w-4" /> Grading Logic
          </TabsTrigger>
          <TabsTrigger value="edit-marks" data-testid="assessment-subtab-edit-marks" className="gap-1.5">
            <Pencil className="h-4 w-4" /> Edit Marks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6"><ActiveTestSettings /></TabsContent>
        <TabsContent value="maxmarks" className="mt-6"><ClassAssessmentConfigTab /></TabsContent>
        <TabsContent value="grading" className="mt-6"><GradingRulesTab /></TabsContent>
        <TabsContent value="edit-marks" className="mt-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Marks Entry</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Enter or correct marks for any test. As admin you can edit published tests too — remember to re-send results afterwards.</p>
          </div>
          <Assessments embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClassAssessmentConfigTab() {
  const { user } = useAuth();
  return <ClassAssessmentConfig isReadOnly={user?.role === "super_admin"} />;
}

function GradingRulesTab() {
  const { user } = useAuth();
  return <GradingRulesSection isReadOnly={user?.role === "super_admin"} />;
}

function ActiveTestSettings() {
  const { user } = useAuth();
  const isReadOnly = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("__none__");
  const [closeDate, setCloseDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/active-exam");
      setData(data);
      setSelected(data.active_key || data.stored_key || "__none__");
      setCloseDate(data.close_date || "");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load assessment settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const exam_key = selected === "__none__" ? null : selected;
      if (exam_key && !closeDate) { toast.error("Please set an auto-close date first."); setSaving(false); return; }
      await api.put("/admin/active-exam", { exam_key, close_date: exam_key ? closeDate : null });
      toast.success(exam_key ? "Marks entry opened for the selected test." : "All tests locked for marks entry.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
  }

  const columns = data?.columns || [];
  const activeLabel = columns.find((c) => c.key === data?.active_key);

  return (
    <div className="max-w-2xl space-y-6" data-testid="active-test-settings">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
          <ClipboardList className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Marks entry — active test</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Choose the one test teachers can currently enter marks for. Other tests stay locked; past tests remain viewable.</p>
        </div>
      </div>

      <div className="max-w-md" data-testid="active-exam-options">
        <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Open test for marks entry</label>
        <Select value={selected} onValueChange={setSelected} disabled={isReadOnly}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="active-exam-select"><SelectValue placeholder="Select a test" /></SelectTrigger>
          <SelectContent>
            {columns.map((c) => (
              <SelectItem key={c.key} value={c.key} data-testid={`active-exam-opt-${c.key}`}>
                {c.label} · {c.short}
              </SelectItem>
            ))}
            <SelectItem value="__none__" data-testid="active-exam-opt-none">None — lock all tests</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!isReadOnly && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="close-date-block">
          <label className="text-sm font-semibold text-slate-900 dark:text-slate-100">Auto-close date {selected !== "__none__" && <span className="text-rose-500">*</span>}</label>
          <p className="text-xs text-slate-500 dark:text-slate-400">Marks entry locks automatically after this date. Required before you can open a test for marks entry.</p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              disabled={selected === "__none__"}
              className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50"
              data-testid="active-exam-close-date"
            />
            {closeDate && (
              <button type="button" onClick={() => setCloseDate("")} className="text-xs text-slate-500 hover:text-rose-500" data-testid="clear-close-date">Clear</button>
            )}
          </div>
        </div>
      )}

      {!isReadOnly && (
        <Button onClick={save} disabled={saving || (selected !== "__none__" && !closeDate)} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50" data-testid="save-active-exam-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save
        </Button>
      )}
    </div>
  );
}

/* ── Grading rules (grade bands + pass mark, used on report cards) ── */
function GradingRulesSection({ isReadOnly }) {
  const [open, setOpen] = useState(false);
  const [openPP, setOpenPP] = useState(false);
  return (
    <div className="space-y-6" data-testid="grading-rules-section">
      {/* Card 1: Classes 1–10 */}
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              Grading rules <span className="text-xs font-normal text-slate-500 ml-1">· Classes 1 – 10</span>
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Define the grade for each percentage range and the per-subject pass mark. Used on report cards.</p>
          </div>
        </div>
        <Button
          onClick={() => setOpen(true)}
          disabled={isReadOnly}
          variant="outline"
          className="rounded-xl"
          data-testid="grading-config-btn"
        >
          <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Configure grading rules
        </Button>
      </div>

      {/* Card 2: Pre-Primary (Nursery / LKG / UKG) */}
      <div className="space-y-3 pt-5 border-t border-slate-200/70 dark:border-slate-700/60">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-300 flex items-center justify-center shrink-0">
            <Baby className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              Pre-Primary grading rules <span className="text-xs font-normal text-slate-500 ml-1">· Nursery, LKG & UKG</span>
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Age-appropriate labels for the little ones. No pass mark — everyone passes. Starts empty; add your own bands.</p>
          </div>
        </div>
        <Button
          onClick={() => setOpenPP(true)}
          disabled={isReadOnly}
          variant="outline"
          className="rounded-xl"
          data-testid="grading-config-pp-btn"
        >
          <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Configure pre-primary rules
        </Button>
      </div>

      <GradingConfigDialog isOpen={open} onClose={() => setOpen(false)} scope="primary" />
      <GradingConfigDialog isOpen={openPP} onClose={() => setOpenPP(false)} scope="pre_primary" />
    </div>
  );
}

/* ── Mandatory subjects + max marks per class ── */
function ClassAssessmentConfig({ isReadOnly }) {
  const [cls, setCls] = useState("5");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [columns, setColumns] = useState([]);
  const [available, setAvailable] = useState([]);
  const [prePrimary, setPrePrimary] = useState(false);
  const [subjects, setSubjects] = useState([]);        // mandatory subject names (from Subjects tab)
  const [maxMarks, setMaxMarks] = useState({});        // {subject: {examKey: "value"}}
  const [ppMax, setPpMax] = useState({});              // pre-primary per-column max {key: "value"}
  const [savingPp, setSavingPp] = useState(false);

  const colDefault = (c) => (c && c.max != null ? c.max : defaultMax(c.key));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assessment-config", { params: { class_name: cls } });
      setColumns(data.columns || []);
      setAvailable(data.available_subjects || []);
      setPrePrimary(!!data.pre_primary);
      setSubjects(data.mandatory_subjects || []);
      const mm = {};
      Object.entries(data.max_marks || {}).forEach(([s, cols]) => {
        mm[s] = {};
        Object.entries(cols || {}).forEach(([k, v]) => { mm[s][k] = String(v); });
      });
      setMaxMarks(mm);
      const pm = {};
      Object.entries(data.pp_max || {}).forEach(([k, v]) => { pm[k] = String(v); });
      setPpMax(pm);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load class config");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls]);

  const savePpMax = async () => {
    setSavingPp(true);
    try {
      const clean = {};
      columns.forEach((c) => { const n = parseFloat(ppMax[c.key]); if (!Number.isNaN(n) && n > 0) clean[c.key] = n; });
      await api.put("/assessments/pre-primary/max-marks", { class_name: cls, max: clean });
      toast.success("Pre-primary max marks saved.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSavingPp(false); }
  };

  const setCell = (subject, key, value) => {
    if (value !== "" && !/^\d{0,3}(\.\d{0,2})?$/.test(value)) return; // decimals, up to 500-ish
    setMaxMarks((prev) => ({ ...prev, [subject]: { ...(prev[subject] || {}), [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      subjects.forEach((s) => {
        const cols = maxMarks[s] || {};
        const cc = {};
        Object.entries(cols).forEach(([k, v]) => {
          const num = parseFloat(v);
          if (!isNaN(num) && num > 0) cc[k] = num;
        });
        if (Object.keys(cc).length) clean[s] = cc;
      });
      await api.put("/admin/assessment-config", { class_name: cls, subjects: [], max_marks: clean });
      toast.success("Max marks saved.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="class-assessment-config">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 flex items-center justify-center shrink-0">
          <Table2 className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Subjects &amp; max marks per class</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Pick the subjects that are eligible for marks in each class, then set the maximum marks for each test. Blank cells use the default (FA tests = 40, Term exams = 80).</p>
        </div>
      </div>

      <div className="max-w-xs">
        <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
        <Select value={cls} onValueChange={setCls}>
          <SelectTrigger data-testid="config-class-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CLASSES.map((c) => <SelectItem key={c} value={c} data-testid={`config-class-opt-${c}`}>{classLabel(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {prePrimary && (
        <div className="rounded-xl border border-pink-300 dark:border-pink-800 bg-pink-50 dark:bg-pink-900/20 p-4 flex items-start gap-3" data-testid="pp-config-note">
          <Info className="h-5 w-5 text-pink-600 dark:text-pink-300 shrink-0 mt-0.5" />
          <div className="text-sm text-pink-800 dark:text-pink-200">
            <b>Pre-primary grade scheme.</b> {classLabel(cls)} is graded on FA-1, FA-2, Mid Term, FA-3, FA-4 and Annual — each mark auto-converts to a grade (Excellent / Very Good / Good / Fair / Need Improvement). <b>Set the max marks for each assessment below</b> (defaults: FA = 25, Mid Term &amp; Annual = 50). This applies to every subject in this class.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
      ) : (
        <>
          {/* Mandatory subjects (managed in the Subjects tab) */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid="config-subjects">
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <BookOpen className="h-4 w-4 text-emerald-500" /> Mandatory subjects (eligible for marks)
            </div>
            {subjects.length === 0 ? (
              <div className="text-sm text-amber-600" data-testid="config-subjects-empty">No mandatory subjects for this class yet. Go to <b>Settings → Subjects</b>, pick this class and tap “Mark mandatory” on the subjects that should carry marks &amp; appear on report cards.</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2" data-testid="config-mandatory-list">
                  {subjects.map((name) => (
                    <span key={name} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 text-sm" data-testid={`config-subject-${name}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> {name}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-slate-400 mt-2">Manage this list in <b>Settings → Subjects</b>. Set each subject's max marks per test below.</div>
              </>
            )}
          </div>

          {/* Pre-primary: editable per-column max marks (uniform across subjects) */}
          {prePrimary && (
            <div className="rounded-xl border border-pink-200 dark:border-pink-800 overflow-x-auto" data-testid="pp-maxmarks">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-200">
                    <th className="text-left font-semibold px-3 py-2.5 sticky left-0 bg-pink-50 dark:bg-pink-900/20">Assessment max marks</th>
                    {columns.map((c) => (
                      <th key={c.key} className="px-2 py-2.5 font-semibold text-center whitespace-nowrap" title={c.label}>{c.short}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300 sticky left-0 bg-white dark:bg-slate-800 whitespace-nowrap">Max marks (all subjects)</td>
                    {columns.map((c) => (
                      <td key={c.key} className="px-1.5 py-1.5 text-center">
                        <Input
                          value={ppMax[c.key] ?? String(colDefault(c))}
                          onChange={(e) => { const v = e.target.value; if (v === "" || /^\d{0,3}(\.\d{0,2})?$/.test(v)) setPpMax((p) => ({ ...p, [c.key]: v })); }}
                          disabled={isReadOnly}
                          inputMode="decimal"
                          className="h-9 w-16 mx-auto text-center rounded-lg"
                          data-testid={`pp-max-${c.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {prePrimary && !isReadOnly && (
            <Button onClick={savePpMax} disabled={savingPp} className="rounded-xl bg-pink-600 hover:bg-pink-700 text-white" data-testid="save-pp-maxmarks-btn">
              {savingPp ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save pre-primary max marks
            </Button>
          )}

          {/* Max marks grid (regular classes) */}
          {!prePrimary && subjects.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto" data-testid="config-maxmarks">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900/40 text-slate-500">
                    <th className="text-left font-semibold px-3 py-2.5 sticky left-0 bg-slate-50 dark:bg-slate-900/40">Subject</th>
                    {columns.map((c) => (
                      <th key={c.key} className="px-2 py-2.5 font-semibold text-center whitespace-nowrap" title={c.label}>{c.short}<div className="text-[10px] font-normal text-slate-400">/{colDefault(c)}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {subjects.map((s) => (
                    <tr key={s} data-testid={`config-maxrow-${s}`}>
                      <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100 sticky left-0 bg-white dark:bg-slate-800 whitespace-nowrap">{s}</td>
                      {columns.map((c) => (
                        <td key={c.key} className="px-1.5 py-1.5 text-center">
                          <Input
                            value={(maxMarks[s]?.[c.key]) ?? ""}
                            onChange={(e) => setCell(s, c.key, e.target.value)}
                            disabled={isReadOnly}
                            placeholder={String(colDefault(c))}
                            inputMode="decimal"
                            className="h-9 w-16 mx-auto text-center rounded-lg"
                            data-testid={`config-max-${s}-${c.key}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isReadOnly && !prePrimary && (
            <Button onClick={save} disabled={saving} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-assessment-config-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save subjects &amp; max marks
            </Button>
          )}
        </>
      )}
    </div>
  );
}
